import { motion } from "framer-motion";
import { CheckCircle2, FileText } from "lucide-react";

const DocumentsSection = ({ data }) => {
  if (!data) return null;

  return (
    <section className="relative overflow-hidden theme-bg-primary pt-10 pb-8">
      {/* ================= Background Glow ================= */}

      <div
        className="
          absolute
          -left-28
          top-20
          h-[260px]
          w-[260px]
          rounded-full
          bg-[color:var(--brand-primary)]/10
          blur-[150px]
        "
      />

      <div
        className="
          absolute
          right-0
          bottom-0
          h-[260px]
          w-[260px]
          rounded-full
          bg-[color:var(--brand-primary)]/10
          blur-[150px]
        "
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        {/* ================= Heading ================= */}

        <motion.div
          initial={{
            opacity: 0,
            y: 35,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
          }}
          viewport={{ once: true }}
          className="mx-auto max-w-3xl text-center"
        >
          <span
            className="
              inline-flex
              items-center
              rounded-full
              border
              border-[color:var(--brand-primary)]/20
              bg-[color:var(--brand-primary)]/10
              px-5
              py-2
              text-xs
              font-semibold
              uppercase
              tracking-[3px]
              brand-color
            "
          >
            Required Documents
          </span>

          <h2
            className="
              mt-5
              text-3xl
              font-bold
              leading-tight
              theme-text-primary
              sm:text-4xl
              lg:text-5xl
            "
          >
            {data.title}
          </h2>

          <p
            className="
              mx-auto
              mt-5
              max-w-2xl
              text-[15px]
              leading-7
              theme-text-muted
              sm:text-base
              lg:text-lg
              lg:leading-8
            "
          >
            Keep these documents ready before starting the registration
            process. Our experts carefully review every document before
            submission to ensure a smooth approval process.
          </p>
        </motion.div>

        {/* ================= Document Cards ================= */}

        <div className="mt-12 grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
          {data.items.map((doc, index) => (
            <motion.div
              key={index}
              initial={{
                opacity: 0,
                y: 25,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.05,
                duration: 0.45,
              }}
              whileHover={{
                y: -5,
              }}
              className="
                group
                rounded-2xl
                border
                theme-border
                bg-[color:var(--bg-tertiary)]
                p-5
                sm:p-6
                transition-all
                duration-300
                hover:border-[color:var(--brand-primary)]/30
                hover:bg-[color:var(--card-bg-secondary)]
                hover:shadow-[0_16px_40px_rgba(0,0,0,0.10)]
              "
            >
              <div className="flex items-start gap-4">
                {/* Number */}

                <div
                  className="
                    flex
                    h-11
                    w-11
                    sm:h-12
                    sm:w-12
                    shrink-0
                    items-center
                    justify-center
                    rounded-2xl
                    bg-[color:var(--brand-primary)]/10
                  "
                >
                  <span className="text-sm font-bold brand-color">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* Content */}

                <div className="flex-1">
                  <h3
                    className="
                      text-base
                      font-semibold
                      leading-7
                      theme-text-primary
                      sm:text-lg
                    "
                  >
                    {doc}
                  </h3>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ================= Bottom Trust Section ================= */}

        <motion.div
          initial={{
            opacity: 0,
            y: 35,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{ once: true }}
          transition={{
            duration: 0.6,
          }}
          className="
            mt-12
            rounded-3xl
            border
            theme-border
            bg-[color:var(--bg-tertiary)]
            p-6
            sm:p-8
            lg:p-10
          "
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {/* Card 1 */}

            <div className="flex items-start gap-4">
              <div
                className="
                  flex
                  h-11
                  w-11
                  sm:h-12
                  sm:w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-[color:var(--brand-primary)]/10
                "
              >
                <CheckCircle2 size={20} className="brand-color" />
              </div>

              <div>
                <h3 className="text-base font-semibold theme-text-primary">
                  Document Verification
                </h3>

                <p className="mt-2 text-sm leading-6 theme-text-muted">
                  Every submitted document is carefully reviewed before
                  filing.
                </p>
              </div>
            </div>

            {/* Card 2 */}

            <div className="flex items-start gap-4">
              <div
                className="
                  flex
                  h-11
                  w-11
                  sm:h-12
                  sm:w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-[color:var(--brand-primary)]/10
                "
              >
                <CheckCircle2 size={20} className="brand-color" />
              </div>

              <div>
                <h3 className="text-base font-semibold theme-text-primary">
                  Expert Assistance
                </h3>

                <p className="mt-2 text-sm leading-6 theme-text-muted">
                  Our professionals help prepare accurate documentation.
                </p>
              </div>
            </div>

            {/* Card 3 */}

            <div className="flex items-start gap-4">
              <div
                className="
                  flex
                  h-11
                  w-11
                  sm:h-12
                  sm:w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-[color:var(--brand-primary)]/10
                "
              >
                <CheckCircle2 size={20} className="brand-color" />
              </div>

              <div>
                <h3 className="text-base font-semibold theme-text-primary">
                  Faster Approval
                </h3>

                <p className="mt-2 text-sm leading-6 theme-text-muted">
                  Proper documentation significantly reduces rejection
                  chances.
                </p>
              </div>
            </div>

            {/* Card 4 */}

            <div className="flex items-start gap-4">
              <div
                className="
                  flex
                  h-11
                  w-11
                  sm:h-12
                  sm:w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-[color:var(--brand-primary)]/10
                "
              >
                <CheckCircle2 size={20} className="brand-color" />
              </div>

              <div>
                <h3 className="text-base font-semibold theme-text-primary">
                  End-to-End Support
                </h3>

                <p className="mt-2 text-sm leading-6 theme-text-muted">
                  From document collection to final government approval.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DocumentsSection;